import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FlaskConical, Play, Pause, RotateCcw, SkipForward, Loader2,
  Users, Clock, Copy, Check, Activity, Save, ListOrdered, ExternalLink,
  GitCompareArrows, Volume2, CheckCheck,
} from 'lucide-react';
import { GlossaryLinkCard } from '../components/GlossaryLinkCard';
import { TTSLab } from '../components/TTSLab';
import { LANGUAGES } from '../lib/languagesCatalog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANG_OPTIONS = LANGUAGES.map((l) => [l.code, l.name]);

const LIMIT_LABELS = {
  segments: 'Segmentos processados',
  source_chars: 'Caracteres (origem)',
  translation_requests: 'Pedidos de tradução',
  tts_requests: 'Pedidos TTS',
  tts_chars: 'Caracteres TTS (est.)',
};

const fmtTime = (s) => {
  if (s === null || s === undefined) return '--:--';
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const DemoBadge = () => (
  <span className="inline-flex items-center gap-1 rounded-full border border-orange-500/50 bg-orange-500/15 text-orange-300 text-[10px] font-bold px-2.5 py-0.5 tracking-widest" data-testid="demo-badge">
    <FlaskConical className="w-3 h-3" /> DEMO
  </span>
);

const CreateDemoForm = ({ onCreated }) => {
  const [name, setName] = useState('');
  const [langs, setLangs] = useState(['en', 'fr']);
  const [limit, setLimit] = useState(10);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [glossaries, setGlossaries] = useState([]);
  const [glossaryId, setGlossaryId] = useState('');
  const [bilateral, setBilateral] = useState(false);

  useEffect(() => {
    fetch(`${API}/glossaries`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then(setGlossaries)
      .catch(() => {});
  }, []);

  const toggle = (l) => setLangs((cur) => (cur.includes(l) ? cur.filter((x) => x !== l) : [...cur, l]));

  const create = async () => {
    setBusy(true);
    setErr('');
    try {
      const r = await fetch(`${API}/demo/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name, target_langs: langs, participant_limit: Number(limit) || 10, glossary_id: glossaryId || null, bilateral, spoken_langs: bilateral ? ['pt', 'en'] : [] }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `Erro ${r.status}`);
      onCreated(data.id);
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto rounded-xl border border-slate-800 bg-slate-900/70 p-6 space-y-5" data-testid="demo-create-form">
      <div className="flex items-center gap-2">
        <FlaskConical className="w-5 h-5 text-orange-400" />
        <h2 className="font-['Manrope'] font-bold text-white">Nova Sessão Demo</h2>
        <DemoBadge />
      </div>
      <p className="text-xs text-slate-400 leading-relaxed">
        Sessão de demonstração isolada, com guião editável, limite de 20 minutos e tradução + voz reais (sem transcrição de áudio). Expira automaticamente.
      </p>
      <div className="space-y-1.5">
        <label className="text-xs text-slate-400">Nome (opcional)</label>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Demo comercial"
          data-testid="demo-name-input"
          className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-500/60" />
      </div>
      <div className="space-y-1.5">
        <label className="text-xs text-slate-400">Idiomas de destino (origem: Português)</label>
        <div className="flex flex-wrap gap-2">
          {LANG_OPTIONS.filter(([l]) => l !== 'pt').map(([l, label]) => (
            <button key={l} onClick={() => toggle(l)} data-testid={`demo-lang-${l}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${langs.includes(l) ? 'border-orange-500/60 bg-orange-500/15 text-orange-300' : 'border-slate-700 text-slate-400 hover:border-slate-500'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-1.5">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={bilateral} onChange={(e) => setBilateral(e.target.checked)}
            className="w-4 h-4 accent-orange-500" data-testid="demo-bilateral-toggle" />
          <span className="text-xs text-slate-300 font-medium">Demo bilateral (PT ⇄ EN) — PoC</span>
        </label>
        {bilateral && (
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Usa um guião misto PT/EN. O idioma de cada segmento é detetado automaticamente; o canal do idioma falado recebe a legenda original sem voz (anti-eco). PT e EN são adicionados aos destinos automaticamente.
          </p>
        )}
      </div>
      <div className="space-y-1.5">
        <label className="text-xs text-slate-400">Limite de participantes (máx. 50)</label>
        <input type="number" min={1} max={50} value={limit} onChange={(e) => setLimit(e.target.value)}
          data-testid="demo-limit-input"
          className="w-28 bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-500/60" />
      </div>
      <div className="space-y-1.5">
        <label className="text-xs text-slate-400">Glossário (opcional — snapshot aplicado à demo)</label>
        <select value={glossaryId} onChange={(e) => setGlossaryId(e.target.value)} data-testid="demo-glossary-select"
          className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-orange-500/60">
          <option value="">Sem glossário</option>
          {glossaries.map((g) => (
            <option key={g.id} value={g.id}>{g.name} (v{g.version}, {g.approved_terms} termos aprovados)</option>
          ))}
        </select>
      </div>
      {err && <div className="text-xs text-red-400" data-testid="demo-create-error">{err}</div>}
      <button onClick={create} disabled={busy || langs.length === 0} data-testid="demo-create-btn"
        className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-orange-600 hover:bg-orange-700 disabled:opacity-40 text-white text-sm font-semibold py-2.5">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
        Criar sessão demo
      </button>
    </div>
  );
};

const VARIANT_LABEL = { without: 'Sem glossário', with: 'Com glossário' };

const ComparisonPanel = ({ sessionId, state }) => {
  const [segIdx, setSegIdx] = useState(0);
  const [lang, setLang] = useState('');
  const [busy, setBusy] = useState(false);
  const [ttsBusy, setTtsBusy] = useState('');
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const [applied, setApplied] = useState('');

  const langs = state.target_langs || [];
  const activeLang = lang || langs[0] || '';
  const paused = !state.playing;
  const ready = paused && !!state.glossary && !state.limit_reached;

  const post = async (path, body) => {
    const r = await fetch(`${API}/demo/sessions/${sessionId}/${path}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.detail || `Erro ${r.status}`);
    return data;
  };

  const compare = async () => {
    setBusy(true); setErr(''); setApplied('');
    try {
      setResult(await post('compare', { segment_index: Number(segIdx), lang: activeLang }));
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const playTts = async (variant) => {
    setTtsBusy(variant); setErr('');
    try {
      const data = await post('compare/tts', { variant });
      new Audio(`data:audio/mp3;base64,${data.audio}`).play();
    } catch (e) { setErr(e.message); }
    setTtsBusy('');
  };

  const apply = async (variant) => {
    setErr('');
    try {
      await post('compare/apply', { variant });
      setApplied(variant);
    } catch (e) { setErr(e.message); }
  };

  const overrides = Object.entries(state.translation_overrides || {}).flatMap(([chunk, byLang]) =>
    Object.entries(byLang || {}).map(([l, o]) => ({ chunk, lang: l, ...o }))
  );

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4 space-y-3" data-testid="demo-comparison-panel">
      <div className="flex flex-wrap items-center gap-2">
        <GitCompareArrows className="w-4 h-4 text-violet-400" />
        <span className="text-sm font-['Manrope'] font-bold text-white">Comparação de Terminologia</span>
        <span className="inline-flex items-center rounded-full border border-violet-500/50 bg-violet-500/15 text-violet-300 text-[9px] font-bold px-2 py-0.5 tracking-widest" data-testid="comparison-badge">COMPARAÇÃO</span>
        <span className="ml-auto text-[10px] text-slate-500">disponível apenas em pausa</span>
      </div>
      <p className="text-[10px] text-amber-400/90" data-testid="comparison-warning">
        ⚠ Cada comparação gera 2 chamadas adicionais de tradução (e 1 de TTS por audição solicitada), contabilizadas nos limites de consumo da demo. Nada é enviado aos participantes.
      </p>
      {!state.glossary && <p className="text-[11px] text-slate-500">Associe um glossário à demo para poder comparar.</p>}
      <div className="flex flex-wrap items-center gap-2">
        <select value={segIdx} onChange={(e) => setSegIdx(e.target.value)} disabled={!ready} data-testid="comparison-segment-select"
          className="flex-1 min-w-[220px] bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-[11px] text-slate-200 focus:outline-none disabled:opacity-50">
          {(state.segments || []).map((s, i) => (
            <option key={i} value={i}>{i + 1}. {s.length > 70 ? `${s.slice(0, 70)}…` : s}</option>
          ))}
        </select>
        <select value={activeLang} onChange={(e) => setLang(e.target.value)} disabled={!ready} data-testid="comparison-lang-select"
          className="bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-[11px] text-slate-200 focus:outline-none disabled:opacity-50">
          {langs.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
        </select>
        <button onClick={compare} disabled={!ready || busy} data-testid="comparison-run-btn"
          className="inline-flex items-center gap-1.5 rounded-md bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white text-xs font-semibold px-4 py-2">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitCompareArrows className="w-3.5 h-3.5" />}
          Comparar com e sem glossário
        </button>
      </div>
      {err && <div className="text-[11px] text-red-400" data-testid="comparison-error">{err}</div>}

      {result && (
        <div className="space-y-3" data-testid="comparison-result">
          <div className="text-[11px] text-slate-400">
            <span className="text-slate-500">Origem (seg. {result.segment_index + 1} · {result.lang.toUpperCase()}):</span>{' '}
            <span className="text-slate-200 italic">"{result.source_text}"</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {['without', 'with'].map((variant) => (
              <div key={variant} className={`rounded-lg border p-3 space-y-2 ${variant === 'with' ? 'border-emerald-600/40 bg-emerald-500/5' : 'border-slate-700 bg-slate-950/50'}`} data-testid={`comparison-${variant}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-bold tracking-wide ${variant === 'with' ? 'text-emerald-300' : 'text-slate-400'}`}>{VARIANT_LABEL[variant].toUpperCase()}</span>
                  {variant === 'with' && <span className="text-[9px] text-slate-500">snapshot v{result.glossary?.version}</span>}
                </div>
                <p className="text-xs text-slate-200 leading-relaxed" data-testid={`comparison-${variant}-text`}>
                  {variant === 'with' ? result.with_glossary : result.without_glossary}
                </p>
                <div className="flex items-center gap-2 pt-1">
                  <button onClick={() => playTts(variant)} disabled={!!ttsBusy || !paused} data-testid={`comparison-tts-${variant}`}
                    className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border border-slate-600 text-slate-300 hover:bg-slate-800 disabled:opacity-40">
                    {ttsBusy === variant ? <Loader2 className="w-3 h-3 animate-spin" /> : <Volume2 className="w-3 h-3" />} Ouvir (TTS)
                  </button>
                  <button onClick={() => apply(variant)} disabled={!paused} data-testid={`comparison-apply-${variant}`}
                    className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border disabled:opacity-40 ${applied === variant ? 'border-emerald-500/60 text-emerald-300 bg-emerald-500/10' : 'border-slate-600 text-slate-300 hover:bg-slate-800'}`}>
                    <CheckCheck className="w-3 h-3" /> {applied === variant ? 'Escolhida para a demo' : 'Usar na demo'}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]">
            <div data-testid="comparison-matched-terms">
              <span className="text-slate-500">Termos do glossário encontrados ({(result.matched_terms || []).length}):</span>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {(result.matched_terms || []).map((t, i) => (
                  <span key={i} className="rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 px-2 py-0.5 text-[10px]">
                    {t.source} → {t.translation}
                  </span>
                ))}
                {(result.matched_terms || []).length === 0 && <span className="text-slate-600">nenhum termo aplicável neste segmento</span>}
              </div>
            </div>
            <div data-testid="comparison-differences">
              <span className="text-slate-500">Diferenças relevantes ({(result.differences || []).length}):</span>
              <div className="space-y-1 pt-1">
                {(result.differences || []).slice(0, 6).map((d, i) => (
                  <div key={i} className="text-[10px]">
                    <span className="text-red-400/80 line-through">{d.without || '∅'}</span>
                    <span className="text-slate-600 mx-1">→</span>
                    <span className="text-emerald-300">{d.with || '∅'}</span>
                  </div>
                ))}
                {(result.differences || []).length === 0 && <span className="text-slate-600">traduções idênticas</span>}
              </div>
            </div>
          </div>
          <p className="text-[9px] text-slate-600" data-testid="comparison-consumption">
            Consumo desta comparação: {result.consumption?.translation_requests} traduções · {result.consumption?.chars_source} caracteres (+1 TTS por audição). Resultado identificado como Comparação — não é um segmento executado nem fica guardado.
          </p>
        </div>
      )}

      {overrides.length > 0 && (
        <div className="text-[10px] text-slate-400 border-t border-slate-800 pt-2" data-testid="comparison-overrides">
          <span className="text-slate-500">Versões escolhidas para a demonstração normal:</span>{' '}
          {overrides.map((o, i) => (
            <span key={i} className="inline-flex items-center rounded-full border border-emerald-600/40 bg-emerald-500/10 text-emerald-300 px-2 py-0.5 mr-1">
              Seg. {(o.segment_index ?? 0) + 1} · {o.lang.toUpperCase()} · {VARIANT_LABEL[o.variant] || o.variant}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default function DemoConsole() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [scriptText, setScriptText] = useState('');
  const [scriptDirty, setScriptDirty] = useState(false);
  const [copied, setCopied] = useState(false);
  const scriptLoadedRef = useRef(false);

  const fetchState = useCallback(async () => {
    try {
      const r = await fetch(`${API}/demo/sessions/${sessionId}/state`, { headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `Erro ${r.status}`);
      setState(data);
      setError('');
      if (!scriptLoadedRef.current && data.segments) {
        setScriptText(data.segments.join('\n'));
        scriptLoadedRef.current = true;
      }
    } catch (e) {
      setError(e.message);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    fetchState();
    const t = setInterval(fetchState, 3000);
    return () => clearInterval(t);
  }, [sessionId, fetchState]);

  const control = async (action) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/demo/sessions/${sessionId}/${action}`, { method: 'POST', headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `Erro ${r.status}`);
      setState((cur) => ({ ...(cur || {}), ...data }));
      setError('');
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  const saveScript = async () => {
    setBusy(true);
    try {
      const segments = scriptText.split('\n').map((s) => s.trim()).filter(Boolean);
      const r = await fetch(`${API}/demo/sessions/${sessionId}/script`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ segments }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `Erro ${r.status}`);
      setScriptDirty(false);
      fetchState();
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/join/${state?.code}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-slate-950 py-10 px-4">
        <div className="max-w-lg mx-auto mb-6">
          <button onClick={() => navigate('/host')} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white" data-testid="demo-back-btn">
            <ArrowLeft className="w-3.5 h-3.5" /> Voltar à consola
          </button>
        </div>
        <CreateDemoForm onCreated={(id) => navigate(`/demo/${id}`)} />
        <TTSLab />
      </div>
    );
  }

  const ended = state && (state.status === 'archived' || state.status === 'stopped');
  const expired = state && state.remaining_s === 0;
  const progress = state && state.total_segments > 0 ? Math.round((state.segment_index / state.total_segments) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-950">
      <header className="border-b border-slate-800 bg-slate-950/95 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/host')} className="text-slate-400 hover:text-white" data-testid="demo-console-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="font-['Manrope'] font-bold text-white text-sm leading-tight flex items-center gap-2">
                {state?.name || 'Sessão Demo'} <DemoBadge />
                {state?.bilateral && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-cyan-500/50 bg-cyan-500/15 text-cyan-300 text-[10px] font-bold px-2.5 py-0.5 tracking-widest" data-testid="demo-bilateral-badge">
                    {(state.spoken_langs || ['pt', 'en']).join(' ⇄ ').toUpperCase()}
                  </span>
                )}
              </div>
              <div className="text-[10px] text-slate-500">
                Código {state?.code || '—'} · idiomas: {(state?.target_langs || []).join(', ').toUpperCase()}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className={`inline-flex items-center gap-1 font-mono ${state?.remaining_s < 120 ? 'text-red-400' : 'text-slate-300'}`} data-testid="demo-remaining-time">
              <Clock className="w-3.5 h-3.5" /> {fmtTime(state?.remaining_s)}
            </span>
            <span className="inline-flex items-center gap-1 text-slate-300" data-testid="demo-participant-count">
              <Users className="w-3.5 h-3.5" /> {state?.participant_count ?? 0}/{state?.participant_limit ?? '—'}
            </span>
            {state?.bilateral && state?.detected_lang && (
              <span className="inline-flex items-center gap-1 text-cyan-300" data-testid="demo-detected-lang">
                Detetado: {state.detected_lang.toUpperCase()}
              </span>
            )}
            <button onClick={() => window.open(`/event/${sessionId}`, '_blank')} data-testid="demo-open-event-mode"
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800">
              <Activity className="w-3 h-3" /> Event Mode
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {error && <div className="rounded-lg border border-red-500/40 bg-red-500/10 text-red-300 text-xs px-4 py-2" data-testid="demo-error">{error}</div>}
        {(ended || expired) && (
          <div className="rounded-lg border border-orange-500/40 bg-orange-500/10 text-orange-300 text-xs px-4 py-2" data-testid="demo-ended-banner">
            Esta demonstração terminou{expired ? ' (limite de 20 minutos atingido)' : ''}. Crie uma nova sessão demo para continuar.
          </div>
        )}
        {state?.limit_reached && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 text-red-300 text-xs px-4 py-2" data-testid="demo-limit-banner">
            Limite de consumo atingido — {LIMIT_LABELS[state.limit_reached.limit] || state.limit_reached.limit} ({state.limit_reached.projected} &gt; {state.limit_reached.cap}).
            A demo foi pausada; reproduzir e avançar estão bloqueados até um administrador repor os limites.
          </div>
        )}
        {state?.recovered === 'resume_paused' && !state.playing && !ended && (
          <div className="rounded-lg border border-sky-500/40 bg-sky-500/10 text-sky-300 text-[11px] px-4 py-2" data-testid="demo-recovered-note">
            Demo recuperada após reinício do serviço — retoma em pausa no primeiro segmento não concluído.
          </div>
        )}
        {state?.recovered === 'script_changed_reset' && !state.playing && !ended && (
          <div className="rounded-lg border border-sky-500/40 bg-sky-500/10 text-sky-300 text-[11px] px-4 py-2" data-testid="demo-recovered-note">
            O guião foi alterado desde a última execução — posição reposta ao início (em pausa).
          </div>
        )}

        {!state ? (
          <div className="flex items-center justify-center py-16 text-slate-500 text-sm"><Loader2 className="w-5 h-5 animate-spin mr-2" /> A carregar…</div>
        ) : (
          <>
            {/* Controls */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4 space-y-4" data-testid="demo-controls-panel">
              <div className="flex flex-wrap items-center gap-2">
                {state.playing ? (
                  <button onClick={() => control('pause')} disabled={busy || ended} data-testid="demo-pause-btn"
                    className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white text-xs font-semibold px-4 py-2">
                    <Pause className="w-3.5 h-3.5" /> Pausar
                  </button>
                ) : (
                  <button onClick={() => control('play')} disabled={busy || ended || !!state.limit_reached} data-testid="demo-play-btn"
                    className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-xs font-semibold px-4 py-2">
                    <Play className="w-3.5 h-3.5" /> {state.segment_index > 0 && !state.finished ? 'Retomar' : 'Iniciar'}
                  </button>
                )}
                <button onClick={() => control('next')} disabled={busy || ended || state.finished || !!state.limit_reached} data-testid="demo-next-btn"
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 text-slate-200 hover:bg-slate-800 disabled:opacity-40 text-xs font-semibold px-4 py-2">
                  <SkipForward className="w-3.5 h-3.5" /> Próximo segmento
                </button>
                <button onClick={() => control('restart')} disabled={busy || ended} data-testid="demo-restart-btn"
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 text-slate-200 hover:bg-slate-800 disabled:opacity-40 text-xs font-semibold px-4 py-2">
                  <RotateCcw className="w-3.5 h-3.5" /> Reiniciar
                </button>
                <span className={`ml-auto text-[11px] font-semibold ${state.playing ? 'text-emerald-400' : 'text-slate-500'}`} data-testid="demo-playing-status">
                  {state.playing ? '● A reproduzir' : state.finished ? 'Guião concluído' : '❚❚ Em pausa'}
                </span>
              </div>
              <div className="space-y-1.5">
                <div className="flex justify-between text-[11px] text-slate-400">
                  <span data-testid="demo-segment-progress">Segmento {state.segment_index}/{state.total_segments}</span>
                  <span>{progress}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                  <div className="h-full bg-orange-500 transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
                {state.current_text && (
                  <p className="text-xs text-slate-300 italic pt-1" data-testid="demo-current-text">"{state.current_text}"</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Script editor */}
              <div className="lg:col-span-2 rounded-xl border border-slate-800 bg-slate-900/70 p-4 space-y-3" data-testid="demo-script-panel">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-['Manrope'] font-bold text-white">
                    <ListOrdered className="w-4 h-4 text-orange-400" /> Guião da demonstração
                    <span className="text-[10px] text-slate-500 font-normal">(1 segmento por linha · editável em pausa)</span>
                  </div>
                  <button onClick={saveScript} disabled={busy || !scriptDirty || state.playing || ended} data-testid="demo-save-script-btn"
                    className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md border border-emerald-600/50 text-emerald-300 hover:bg-emerald-600/15 disabled:opacity-40">
                    <Save className="w-3 h-3" /> Guardar guião
                  </button>
                </div>
                <textarea
                  value={scriptText}
                  onChange={(e) => { setScriptText(e.target.value); setScriptDirty(true); }}
                  disabled={state.playing || ended}
                  rows={12}
                  data-testid="demo-script-textarea"
                  className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-200 font-mono leading-relaxed focus:outline-none focus:border-orange-500/60 disabled:opacity-50"
                />
              </div>

              {/* Side: join + glossary + consumption */}
              <div className="space-y-4">
                <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4 space-y-2" data-testid="demo-join-card">
                  <div className="text-sm font-['Manrope'] font-bold text-white">Participantes</div>
                  <p className="text-[11px] text-slate-400">Link de acesso (limite {state.participant_limit} participantes):</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-[11px] text-orange-300 bg-slate-950 border border-slate-800 rounded px-2 py-1.5 truncate">/join/{state.code}</code>
                    <button onClick={copyLink} data-testid="demo-copy-link-btn" className="p-1.5 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800">
                      {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => window.open(`/join/${state.code}`, '_blank')} className="p-1.5 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="demo-open-join-btn">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <GlossaryLinkCard sessionId={sessionId} />
                <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4 space-y-1.5" data-testid="demo-consumption-card">
                  <div className="text-sm font-['Manrope'] font-bold text-white mb-1">Consumo da demo</div>
                  {Object.entries(state.limits || {}).map(([key, v]) => (
                    <div key={key} className="flex justify-between text-[11px]" data-testid={`demo-limit-${key}`}>
                      <span className="text-slate-500">{LIMIT_LABELS[key] || key}</span>
                      <span className={`font-semibold ${v.cap && v.used >= v.cap ? 'text-red-400' : 'text-slate-300'}`}>
                        {v.used ?? 0}{v.cap ? ` / ${v.cap}` : ''}
                      </span>
                    </div>
                  ))}
                  {(state.stats?.failed_attempts ?? 0) > 0 && (
                    <div className="flex justify-between text-[11px]" data-testid="demo-failed-attempts">
                      <span className="text-slate-500">Tentativas falhadas (com consumo)</span>
                      <span className="text-amber-400 font-semibold">{state.stats.failed_attempts}</span>
                    </div>
                  )}
                  <p className="text-[9px] text-slate-600 pt-1">Limites absolutos por sessão demo — não reiniciam com restart, pausa ou edição do guião. Tradução + voz reais.</p>
                </div>
              </div>
            </div>

            {/* Bloco D2: terminology comparison (paused only, never sent to participants) */}
            {!ended && <ComparisonPanel sessionId={sessionId} state={state} />}
          </>
        )}
      </main>
    </div>
  );
}
